import { Box } from "@mui/material";
import { enqueueSnackbar } from "notistack";

import { getBackendErrorMessage } from "./backendError";
import { DemoModeBlockedError } from "./demoMode";
import { ErrorDetailsButton } from "./ErrorDetailsButton";
import { KNOWN_ERROR_FIXES, type KnownErrorMatch } from "./knownErrorFixes";

interface ErrorSnackbarOptions {
  onRetry?: () => void;
}

/**
 * Displays an error snackbar with automatic error message extraction.
 *
 * @param error - The error object to extract message from
 * @param fallbackMessage - Message to display if error has no extractable message
 * @param options - Optional configuration (e.g., retry callback)
 *
 * @example
 * // Basic usage
 * enqueueErrorSnackbar(error, "Failed to save");
 *
 * @example
 * // With retry
 * enqueueErrorSnackbar(error, "Failed to save", {
 *   onRetry: () => mutate(variables)
 * });
 */
export function enqueueErrorSnackbar(
  error: unknown,
  fallbackMessage: string,
  options?: ErrorSnackbarOptions,
): void {
  // Demo-mode blocks already showed their own notice — no error toast on top.
  if (error instanceof DemoModeBlockedError) return;
  const fullMessage = getBackendErrorMessage(error) || fallbackMessage;
  const MAX_LENGTH = 90;

  const matchedKey = (Object.keys(KNOWN_ERROR_FIXES) as KnownErrorMatch[]).find(
    (key) => fullMessage.includes(key),
  );

  const knownFix = matchedKey ? KNOWN_ERROR_FIXES[matchedKey] : undefined;

  const shouldShowDetails = Boolean(
    knownFix || fullMessage.length > MAX_LENGTH,
  );

  const displayMessage =
    fullMessage.length > MAX_LENGTH ? (
      <Box
        sx={{
          width: "100%",
          maxWidth: "50vw",
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
        }}
      >
        {fullMessage.substring(0, MAX_LENGTH)}...
      </Box>
    ) : (
      fullMessage
    );

  if (knownFix) {
    enqueueSnackbar(knownFix.title, {
      variant: knownFix.snackbarVariant || "error",
      ...options,
      persist: shouldShowDetails,
      action: shouldShowDetails ? (
        <ErrorDetailsButton fullMessage={fullMessage} knownFix={knownFix} />
      ) : undefined,
    });
  } else {
    enqueueSnackbar(displayMessage, {
      variant: "error",
      ...options,
      persist: shouldShowDetails,
      action: shouldShowDetails ? (
        <ErrorDetailsButton fullMessage={fullMessage} knownFix={knownFix} />
      ) : undefined,
    });
  }
}
